import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isAxiosError } from 'axios'
import { api, type ValidationProblemDetailsWithCodes } from '@/api/eOrdersApi'
import { useRequestConfig } from '@/composables/requestConfig'
import { showSuccess, showError } from '@/utils/utils'

export interface ValidationResult {
  fileName: string
  errors: { field: string; errorCode: string; errorMessage: string }[]
}

export function useSapValidation() {
  const { t } = useI18n()
  const requestConfig = useRequestConfig()
  
  const isValidating = ref(false)
  const validatingFileName = ref('')
  const validationResult = ref<ValidationResult | null>(null)
  const showValidationModal = ref(false)
  
  async function validateFile(fileName: string) {
    isValidating.value = true
    validatingFileName.value = fileName
    
    try {
      await api.sapDownloadValidate(fileName, requestConfig)
      
      showSuccess(t('sapImport.validation.valid'))
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined
      const data = isAxiosError(err) ? err.response?.data : undefined
      
      if (status === 422 && data?.detail && !data?.errors) {
        showError(data.detail)
      } else if (status === 422 && data?.errors) {
        const body = data as ValidationProblemDetailsWithCodes
        const errors: ValidationResult['errors'] = []
        
        for (const [field, details] of Object.entries(body.errors)) {
          for (const detail of details) {
            errors.push({
              field,
              errorCode: detail.errorCode ?? '',
              errorMessage: detail.errorMessage
            })
          }
        }
        
        validationResult.value = { fileName, errors }
        showValidationModal.value = true
      } else if (status === 404) {
        showError(t('sapImport.validation.notFound'))
      } else if (status === 503) {
        showError(t('sapImport.validation.unavailable'))
      } else {
        showError(t('sapImport.validation.failed'))
      }
    } finally {
      isValidating.value = false
      validatingFileName.value = ''
    }
  }
  
  return {
    isValidating,
    validatingFileName,
    validationResult,
    showValidationModal,
    validateFile
  }
}